import { useState } from "react";
import type { AdminClient, Employee } from "@/hooks/useAdminAuth";

interface ClientAssignModalsProps {
  // Single assign
  assignClient: AdminClient | null;
  employees: Employee[];
  selectedAdvisor: string;
  assignLoading: boolean;
  assignError: string;
  onAssignClose: () => void;
  onAdvisorChange: (v: string) => void;
  onAssignConfirm: () => void;
  // Batch assign
  showBatchConfirm: boolean;
  selectedCount: number;
  onBatchConfirmClose: () => void;
  onBatchConfirmProceed: () => void;
  showBatchModal: boolean;
  batchAdvisor: string;
  batchLoading: boolean;
  batchError: string;
  batchProgress: { current: number; total: number; failed: number } | null;
  onBatchModalClose: () => void;
  onBatchAdvisorChange: (v: string) => void;
  onBatchAssign: () => void;
  // Delete
  deleteConfirm: number | null;
  onDeleteClose: () => void;
  onDeleteConfirm: () => void;
  // Batch delete
  showBatchDelete: boolean;
  batchDeleteLoading: boolean;
  batchDeleteProgress: { current: number; total: number; failed: number } | null;
  onBatchDeleteClose: () => void;
  onBatchDeleteConfirm: () => void;
}

export default function ClientAssignModals({
  assignClient,
  employees,
  selectedAdvisor,
  assignLoading,
  assignError,
  onAssignClose,
  onAdvisorChange,
  onAssignConfirm,
  showBatchConfirm,
  selectedCount,
  onBatchConfirmClose,
  onBatchConfirmProceed,
  showBatchModal,
  batchAdvisor,
  batchLoading,
  batchError,
  batchProgress,
  onBatchModalClose,
  onBatchAdvisorChange,
  onBatchAssign,
  deleteConfirm,
  onDeleteClose,
  onDeleteConfirm,
  showBatchDelete,
  batchDeleteLoading,
  batchDeleteProgress,
  onBatchDeleteClose,
  onBatchDeleteConfirm,
}: ClientAssignModalsProps) {
  return (
    <>
      {/* Single Assign Modal */}
      {assignClient && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-[#1a3a5c]">分配顾问</h3>
              <button onClick={onAssignClose} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 cursor-pointer"><i className="ri-close-line"></i></button>
            </div>
            <p className="text-xs text-gray-500 mb-4">为客户 <span className="font-medium text-[#1a3a5c]">{assignClient.name}</span> 分配专属顾问</p>
            {assignError && <div className="bg-red-50 text-red-600 text-xs rounded-lg px-3 py-2 mb-3 flex items-center gap-1.5"><i className="ri-error-warning-line"></i>{assignError}</div>}
            <label className="block text-xs font-medium text-gray-500 mb-1.5">选择顾问</label>
            <select value={selectedAdvisor} onChange={(e) => onAdvisorChange(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm text-[#1a3a5c] focus:outline-none focus:border-[#c9a96e] bg-white cursor-pointer mb-5">
              <option value="">请选择顾问</option>
              {employees.map((e) => <option key={e.id} value={String(e.id)}>{e.name}{e.phone ? ` · ${e.phone}` : ""}</option>)}
            </select>
            <div className="flex gap-3 justify-end">
              <button onClick={onAssignClose} className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={onAssignConfirm} disabled={assignLoading || !selectedAdvisor} className="px-5 py-2 bg-[#1a3a5c] text-white rounded-lg text-sm font-medium hover:bg-[#142d48] transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap">
                {assignLoading ? <><i className="ri-loader-4-line animate-spin mr-1"></i>分配中...</> : "确认分配"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteConfirm !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-xl text-center">
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-3"><i className="ri-error-warning-line text-red-500 text-xl"></i></div>
            <h3 className="text-base font-bold text-[#1a3a5c] mb-1">确认删除客户？</h3>
            <p className="text-xs text-gray-400 mb-5">此操作将永久删除该客户及其所有进度、通知数据，不可恢复。</p>
            <div className="flex gap-3 justify-center">
              <button onClick={onDeleteClose} className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={onDeleteConfirm} className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap">确认删除</button>
            </div>
          </div>
        </div>
      )}

      {/* Batch Assign Confirm */}
      {showBatchConfirm && (
        <div className="fixed inset-0 z-[55] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-xl text-center">
            <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-3"><i className="ri-user-star-line text-amber-500 text-xl"></i></div>
            <h3 className="text-base font-bold text-[#1a3a5c] mb-1">确认批量分配顾问？</h3>
            <p className="text-xs text-gray-400 mb-5">将为已选的 <span className="font-semibold text-[#c9a96e]">{selectedCount}</span> 位客户统一分配专属顾问，分配后原顾问将被覆盖。</p>
            <div className="flex gap-3 justify-center">
              <button onClick={onBatchConfirmClose} className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={onBatchConfirmProceed} className="px-4 py-2 bg-[#c9a96e] text-white rounded-lg text-sm font-medium hover:bg-[#b8944f] transition-colors cursor-pointer whitespace-nowrap">确认分配</button>
            </div>
          </div>
        </div>
      )}

      {/* Batch Assign Modal */}
      {showBatchModal && (
        <div className="fixed inset-0 z-[55] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4"><h3 className="text-base font-bold text-[#1a3a5c]">批量分配顾问</h3><button onClick={onBatchModalClose} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 cursor-pointer"><i className="ri-close-line"></i></button></div>
            <p className="text-xs text-gray-500 mb-4">为已选的 <span className="font-semibold text-[#1a3a5c]">{selectedCount}</span> 位客户统一分配顾问</p>
            {batchError && <div className="bg-red-50 text-red-600 text-xs rounded-lg px-3 py-2 mb-3 flex items-center gap-1.5"><i className="ri-error-warning-line"></i>{batchError}</div>}
            {batchProgress && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
                <div className="flex items-center justify-between text-xs mb-1"><span className="text-amber-700">处理中...</span><span className="text-amber-600 font-medium">{batchProgress.current}/{batchProgress.total}{batchProgress.failed > 0 ? ` · ${batchProgress.failed} 失败` : ""}</span></div>
                <div className="w-full h-1.5 bg-amber-200 rounded-full overflow-hidden"><div className="h-full bg-[#c9a96e] rounded-full transition-all" style={{ width: `${(batchProgress.current / batchProgress.total) * 100}%` }}></div></div>
              </div>
            )}
            <label className="block text-xs font-medium text-gray-500 mb-1.5">选择顾问</label>
            <select value={batchAdvisor} onChange={(e) => onBatchAdvisorChange(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm text-[#1a3a5c] focus:outline-none focus:border-[#c9a96e] bg-white cursor-pointer mb-5"><option value="">请选择顾问</option>{employees.map((e) => <option key={e.id} value={String(e.id)}>{e.name}{e.phone ? ` · ${e.phone}` : ""}</option>)}</select>
            <div className="flex gap-3 justify-end"><button onClick={onBatchModalClose} className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">取消</button><button onClick={onBatchAssign} disabled={batchLoading || !batchAdvisor} className="px-5 py-2 bg-[#c9a96e] text-white rounded-lg text-sm font-medium hover:bg-[#b8944f] transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap">{batchLoading ? <><i className="ri-loader-4-line animate-spin mr-1"></i>分配中...</> : "确认分配"}</button></div>
          </div>
        </div>
      )}

      {/* Batch Delete Modal */}
      {showBatchDelete && (
        <div className="fixed inset-0 z-[55] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-xl text-center">
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-3"><i className="ri-error-warning-line text-red-500 text-xl"></i></div>
            <h3 className="text-base font-bold text-[#1a3a5c] mb-1">确认批量删除？</h3>
            <p className="text-xs text-gray-400 mb-2">将永久删除已选的 <span className="font-semibold text-red-500">{selectedCount}</span> 位客户及其所有进度、通知数据，不可恢复。</p>
            {batchDeleteProgress && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-3">
                <div className="flex items-center justify-between text-xs mb-1"><span className="text-red-700">删除中...</span><span className="text-red-600 font-medium">{batchDeleteProgress.current}/{batchDeleteProgress.total}{batchDeleteProgress.failed > 0 ? ` · ${batchDeleteProgress.failed} 失败` : ""}</span></div>
                <div className="w-full h-1.5 bg-red-200 rounded-full overflow-hidden"><div className="h-full bg-red-500 rounded-full transition-all" style={{ width: `${(batchDeleteProgress.current / batchDeleteProgress.total) * 100}%` }}></div></div>
              </div>
            )}
            <div className="flex gap-3 justify-center mt-5"><button onClick={onBatchDeleteClose} className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">取消</button><button onClick={onBatchDeleteConfirm} disabled={batchDeleteLoading} className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap">{batchDeleteLoading ? <><i className="ri-loader-4-line animate-spin mr-1"></i>删除中...</> : "确认删除"}</button></div>
          </div>
        </div>
      )}
    </>
  );
}